import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Save, ShieldCheck, ShieldX } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FileImage } from "@/components/FileImage";
import { api } from "@/lib/api";
import { fmtINR, fmtDate, fmtMonth, STATUS_STYLES, STATUS_LABELS } from "@/lib/format";

const TABS = ["Overview", "Bookings", "Requests", "Customers", "Services"];

const ID_STATUS_STYLES = {
  pending: "bg-amberwarm/15 text-amberwarm",
  verified: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};
const ID_STATUS_LABELS = { pending: "Pending review", verified: "Verified", rejected: "Rejected" };

const Stat = ({ label, value }) => (
  <div className="rounded-3xl border border-ink/8 bg-white p-7">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className="mt-2 font-display text-4xl font-medium text-ink">{value}</p>
  </div>
);

const BookingRow = ({ booking, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(booking.status);
  const [adminNotes, setAdminNotes] = useState(booking.admin_notes || "");
  const [discountAmt, setDiscountAmt] = useState(booking.discount_amount || 0);
  const [discountNote, setDiscountNote] = useState(booking.discount_note || "");
  const [saving, setSaving] = useState(false);
  const b = booking;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${b.booking_id}`, {
        status,
        admin_notes: adminNotes,
        discount_amount: Number(discountAmt) || 0,
        discount_note: discountNote,
      });
      toast.success("Booking updated");
      onSaved();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const resolveRequest = async (action) => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${b.booking_id}/change-request`, { action });
      toast.success(action === "approve" ? "Request approved" : "Request rejected");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resolve request");
    } finally {
      setSaving(false);
    }
  };

  const pendingRequest = b.change_request?.status === "pending" ? b.change_request : null;

  return (
    <div data-testid={`admin-booking-${b.booking_id}`} className="rounded-3xl border border-ink/8 bg-white">
      <button
        type="button"
        data-testid={`admin-booking-toggle-${b.booking_id}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center justify-between gap-4 p-6 text-left"
      >
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
            {STATUS_LABELS[b.status]}
          </span>
          <div>
            <p className="text-sm font-semibold text-ink">
              {b.customer?.name || "—"} · {b.pups.map((p) => p.name).join(", ")}
            </p>
            <p className="text-xs text-slate-500">
              {b.items.map((i) => `${i.service_name} (${fmtDate(i.start_date)}${i.end_date !== i.start_date ? ` → ${fmtDate(i.end_date)}` : ""})`).join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-sm font-semibold text-ink">
            {fmtINR(b.amount_paid)} <span className="font-normal text-slate-400">/ {fmtINR(b.total)}</span>
          </p>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </div>
      </button>

      {open && (
        <div className="border-t border-ink/8 p-6">
          {pendingRequest && (
            <div
              data-testid={`admin-change-request-${b.booking_id}`}
              className="mb-6 rounded-2xl border border-amberwarm/40 bg-amberwarm/10 p-5"
            >
              <p className="text-sm font-semibold text-ink">
                {pendingRequest.type === "cancel" ? "Cancellation requested" : "Change requested"}
                {pendingRequest.type === "cancel" && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    Refund estimate: {fmtINR(pendingRequest.refund_estimate)} (
                    {pendingRequest.refund_policy === "full" ? "100% — more than 24h before check-in" : "75% — within 24h of check-in"})
                  </span>
                )}
              </p>
              {pendingRequest.message && (
                <p className="mt-1 text-sm text-slate-600">“{pendingRequest.message}”</p>
              )}
              <div className="mt-3 flex gap-3">
                <button
                  type="button"
                  data-testid={`admin-approve-${b.booking_id}`}
                  onClick={() => resolveRequest("approve")}
                  disabled={saving}
                  className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-700 disabled:opacity-60"
                >
                  Approve
                </button>
                <button
                  type="button"
                  data-testid={`admin-reject-${b.booking_id}`}
                  onClick={() => resolveRequest("reject")}
                  disabled={saving}
                  className="rounded-full border border-ink/15 px-5 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-red-300 hover:text-red-500 disabled:opacity-60"
                >
                  Reject
                </button>
              </div>
            </div>
          )}
          <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Pet parent</p>
            <p className="mt-2 text-sm font-medium text-ink">{b.customer?.name}</p>
            <p className="text-sm text-slate-600">{b.customer?.email}{b.customer?.phone ? ` · ${b.customer.phone}` : ""}</p>

            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Pups</p>
            <ul className="mt-2 space-y-2">
              {b.pups.map((p) => (
                <li key={p.pup_id} className="rounded-2xl bg-cream-dark/60 p-4 text-sm">
                  <span className="font-semibold text-ink">{p.name}</span>
                  <span className="text-slate-600">
                    {" "}· {[p.breed, p.gender, p.weight_kg ? `${p.weight_kg} kg` : null].filter(Boolean).join(" · ")}
                  </span>
                  {p.medications && <p className="mt-1 text-xs text-bark">Meds: {p.medications}</p>}
                  {p.medical_conditions && <p className="mt-0.5 text-xs text-bark">Conditions: {p.medical_conditions}</p>}
                  {p.temperament?.length > 0 && <p className="mt-0.5 text-xs text-slate-500">Temperament: {Array.isArray(p.temperament) ? p.temperament.join(", ") : p.temperament}</p>}
                </li>
              ))}
            </ul>

            {b.notes && (
              <>
                <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Parent notes</p>
                <p className="mt-2 text-sm text-slate-600">{b.notes}</p>
              </>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Payments</p>
            <ul className="mt-2 space-y-2">
              {(b.payments || []).filter((p) => p.status === "paid").map((p) => (
                <li key={p.payment_id} className="flex justify-between rounded-2xl bg-emerald-50 px-4 py-2.5 text-xs">
                  <span className="font-semibold capitalize text-emerald-800">{p.kind}{p.demo ? " (demo)" : ""}</span>
                  <span className="text-emerald-700">{fmtINR(p.amount)}</span>
                </li>
              ))}
              {!(b.payments || []).some((p) => p.status === "paid") && (
                <li className="text-sm text-slate-400">No payments yet</li>
              )}
            </ul>

            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Discount</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  data-testid={`admin-discount-${b.booking_id}`}
                  value={discountAmt}
                  onChange={(e) => setDiscountAmt(e.target.value)}
                  className="w-28 rounded-xl border border-ink/15 bg-cream px-3 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
                />
              </div>
              <input
                type="text"
                data-testid={`admin-discount-note-${b.booking_id}`}
                value={discountNote}
                onChange={(e) => setDiscountNote(e.target.value)}
                placeholder="Reason (e.g. loyalty, rescue pup)"
                className="min-w-[180px] flex-1 rounded-xl border border-ink/15 bg-cream px-3 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none"
              />
            </div>
            {Number(discountAmt) > 0 && (
              <p className="mt-1.5 text-xs font-semibold text-emerald-600">
                Customer pays {fmtINR(b.total - (Number(discountAmt) || 0))} instead of {fmtINR(b.total)}
              </p>
            )}

            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Status</p>
            <select
              data-testid={`admin-status-${b.booking_id}`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
            >
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>

            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Admin notes</p>
            <textarea
              data-testid={`admin-notes-${b.booking_id}`}
              rows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Internal notes…"
              className="mt-2 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
            />
            <button
              type="button"
              data-testid={`admin-save-${b.booking_id}`}
              onClick={save}
              disabled={saving}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-60"
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceEditor = ({ service, onSaved }) => {
  const [tiers, setTiers] = useState(service.tiers);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/services/${service.service_id}`, { tiers });
      toast.success(`${service.name} pricing updated`);
      onSaved();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`admin-service-${service.slug}`} className="rounded-3xl border border-ink/8 bg-white p-7">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display text-xl font-medium text-ink">{service.name}</p>
          <p className="text-xs uppercase tracking-wider text-slate-400">per {service.unit}</p>
        </div>
        <button
          type="button"
          data-testid={`admin-service-save-${service.slug}`}
          onClick={save}
          disabled={saving}
          className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="mt-5 space-y-3">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="flex-1 text-sm text-slate-600">{t.label}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-slate-400">₹</span>
              <input
                type="number"
                min="0"
                data-testid={`admin-tier-${service.slug}-${i}`}
                value={t.price}
                onChange={(e) =>
                  setTiers((cur) => cur.map((x, j) => (j === i ? { ...x, price: Number(e.target.value) } : x)))
                }
                className="w-28 rounded-xl border border-ink/15 bg-cream px-3 py-2 text-sm text-ink focus:border-bark focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HEALTH_FIELDS = [
  ["vaccination_last_date", "Vaccination (last)"],
  ["vaccination_next_due", "Vaccination due"],
  ["kennel_cough_date", "Kennel cough vaccine"],
  ["tick_treatment_last", "Tick & flea (last)"],
  ["tick_treatment_next", "Tick & flea due"],
  ["deworming_last", "Deworming (last)"],
  ["deworming_next", "Deworming due"],
];

const FlagNoteEditor = ({ flag, note, onSave, testid }) => {
  const [f, setF] = useState(flag);
  const [n, setN] = useState(note || "");
  const [saving, setSaving] = useState(false);
  return (
    <div className="mt-3 rounded-2xl bg-cream p-4">
      <label className="flex items-center gap-2.5 text-xs font-semibold text-navy">
        <input
          type="checkbox"
          data-testid={`${testid}-flag`}
          checked={f}
          onChange={(e) => setF(e.target.checked)}
          className="h-4 w-4 accent-[#EA580C]"
        />
        Flag for attention
      </label>
      <textarea
        rows={2}
        data-testid={`${testid}-note`}
        value={n}
        onChange={(e) => setN(e.target.value)}
        placeholder="Internal note — behaviour, instructions, follow-ups…"
        className="mt-2 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-xs text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none"
      />
      <button
        type="button"
        data-testid={`${testid}-save`}
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          try {
            await onSave(f, n);
            toast.success("Saved");
          } catch {
            toast.error("Could not save");
          } finally {
            setSaving(false);
          }
        }}
        className="mt-2 rounded-full bg-navy px-4 py-2 text-[11px] font-semibold text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save flag & note"}
      </button>
    </div>
  );
};

const CustomerCard = ({ c, onSaved }) => {
  const [open, setOpen] = useState(false);
  const verifyId = async (action) => {
    if (
      action === "reject" &&
      !window.confirm(
        "Reject this photo ID? The customer will be flagged and all their upcoming bookings cancelled (amounts paid refunded in full)."
      )
    )
      return;
    try {
      const r = await api.post(`/admin/customers/${c.user_id}/id-verification`, { action });
      toast.success(
        action === "verify" ? "ID verified" : `ID rejected — ${r.data.bookings_cancelled} booking(s) cancelled`
      );
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };
  return (
    <div className="rounded-3xl border border-ink/8 bg-white">
      <button
        type="button"
        data-testid={`admin-customer-${c.user_id}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center justify-between gap-4 p-6 text-left"
      >
        <div className="flex items-center gap-3">
          {c.admin_flag && (
            <span className="rounded-full bg-red-100 px-3 py-1 text-[11px] font-semibold text-red-600">Flagged</span>
          )}
          <div>
            <p className="text-sm font-semibold text-ink">{c.name}</p>
            <p className="text-xs text-slate-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          {c.pups.length} {c.pups.length === 1 ? "pup" : "pups"} · {c.bookings_count} bookings
        </p>
      </button>
      {open && (
        <div className="border-t border-ink/8 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Admin tools</p>
          <FlagNoteEditor
            testid={`admin-customer-tools-${c.user_id}`}
            flag={!!c.admin_flag}
            note={c.admin_note}
            onSave={async (f, n) => {
              await api.put(`/admin/customers/${c.user_id}`, { admin_flag: f, admin_note: n });
              onSaved();
            }}
          />
          {c.admin_note && (
            <p className="mt-3 rounded-xl bg-amberwarm/10 px-4 py-2.5 text-xs text-slate-600">
              Note: {c.admin_note}
            </p>
          )}

          <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">Parent details</p>
          <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
            <p className="rounded-xl bg-cream-dark/60 px-4 py-2.5">
              <span className="font-semibold text-ink">Address:</span> {c.address || "—"}
            </p>
            <p className="rounded-xl bg-cream-dark/60 px-4 py-2.5">
              <span className="font-semibold text-ink">Emergency contact:</span>{" "}
              {c.emergency_contact_name
                ? `${c.emergency_contact_name} · ${c.emergency_contact_phone || "—"}`
                : "—"}
            </p>
            <p className="rounded-xl bg-cream-dark/60 px-4 py-2.5">
              <span className="font-semibold text-ink">Vet:</span>{" "}
              {c.vet_name ? `${c.vet_name} · ${c.vet_phone || "—"}` : "—"}
            </p>
          </div>

          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Photo ID verification</p>
          {c.photo_id ? (
            <div
              data-testid={`admin-photoid-${c.user_id}`}
              className="mt-3 flex flex-wrap items-center gap-4 rounded-2xl bg-cream-dark/60 p-4"
            >
              {c.photo_id.content_type?.startsWith("image/") ? (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.photo_id.storage_path}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FileImage path={c.photo_id.storage_path} alt="Photo ID" className="h-16 w-16 rounded-xl object-cover" />
                </a>
              ) : (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.photo_id.storage_path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-16 w-16 items-center justify-center rounded-xl bg-white text-[10px] font-bold text-slate-500"
                >
                  View PDF
                </a>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{c.photo_id.original_filename}</p>
                <span
                  data-testid={`admin-id-status-${c.user_id}`}
                  className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${ID_STATUS_STYLES[c.id_verified] || ID_STATUS_STYLES.pending}`}
                >
                  {ID_STATUS_LABELS[c.id_verified] || "Pending review"}
                </span>
              </div>
              <div className="flex gap-2">
                {c.id_verified !== "verified" && (
                  <button
                    type="button"
                    data-testid={`admin-id-verify-${c.user_id}`}
                    onClick={() => verifyId("verify")}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-[11px] font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    Verify
                  </button>
                )}
                {c.id_verified !== "rejected" && (
                  <button
                    type="button"
                    data-testid={`admin-id-reject-${c.user_id}`}
                    onClick={() => verifyId("reject")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-[11px] font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <ShieldX className="h-3.5 w-3.5" aria-hidden="true" />
                    Reject
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-400">No photo ID uploaded.</p>
          )}

          <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">Policy agreements</p>
          {(c.policy_ack_log || []).length > 0 ? (
            <div data-testid={`admin-policy-acks-${c.user_id}`} className="mt-3 space-y-2">
              {(c.policy_ack_log || []).map((a) => (
                <div
                  key={a.service_slug}
                  data-testid={`admin-policy-ack-${c.user_id}-${a.service_slug}`}
                  className="flex items-center justify-between gap-3 rounded-xl bg-cream-dark/60 px-4 py-2.5 text-xs text-slate-600"
                >
                  <span className="flex items-center gap-1.5 font-semibold text-ink">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
                    {a.service_name}
                  </span>
                  <span className="text-slate-500">
                    Agreed{" "}
                    {new Date(a.agreed_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-400">No policies agreed yet.</p>
          )}

          <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">Pups</p>
          {c.pups.length === 0 ? (
            <p className="mt-2 text-sm text-slate-400">No pups registered yet.</p>
          ) : (
            <ul className="mt-3 space-y-4">
              {c.pups.map((p) => (
                <li key={p.pup_id} data-testid={`admin-pup-${p.pup_id}`} className="rounded-2xl bg-cream-dark/60 p-5 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-ink">{p.name}</p>
                    {p.admin_flag && (
                      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[10px] font-semibold text-red-600">Flagged</span>
                    )}
                    <p className="text-slate-600">
                      {[p.breed, p.gender, p.weight_kg ? `${p.weight_kg} kg` : null].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {(p.medications || p.medical_conditions || p.food_allergies) && (
                    <div className="mt-2 space-y-0.5 text-xs text-bark">
                      {p.medications && <p>Meds: {p.medications}</p>}
                      {p.medical_conditions && <p>Conditions: {p.medical_conditions}</p>}
                      {p.food_allergies && <p>Allergies: {p.food_allergies}</p>}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {HEALTH_FIELDS.map(([key, label]) => (
                      <div key={key} className="rounded-xl bg-white px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                        <p className="mt-0.5 text-xs font-medium text-ink">{p[key] ? fmtMonth(p[key]) : "—"}</p>
                      </div>
                    ))}
                  </div>
                  {p.temperament?.length > 0 && <p className="mt-2 text-xs text-slate-500">Temperament: {Array.isArray(p.temperament) ? p.temperament.join(", ") : p.temperament}</p>}
                  <p className="mt-1 text-xs text-slate-500">{(p.documents || []).length} files attached</p>
                  {p.admin_note && (
                    <p className="mt-2 rounded-xl bg-amberwarm/10 px-3 py-2 text-xs text-slate-600">Note: {p.admin_note}</p>
                  )}
                  <FlagNoteEditor
                    testid={`admin-pup-tools-${p.pup_id}`}
                    flag={!!p.admin_flag}
                    note={p.admin_note}
                    onSave={async (f, n) => {
                      await api.put(`/admin/pups/${p.pup_id}`, { admin_flag: f, admin_note: n });
                      onSaved();
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const AddonManager = ({ addons, onSaved }) => {
  const [drafts, setDrafts] = useState({});
  const [form, setForm] = useState({ name: "", price: "", unit: "stay", description: "" });
  const [busy, setBusy] = useState(false);

  const savePrice = async (a) => {
    try {
      await api.put(`/admin/addons/${a.addon_id}`, { price: Number(drafts[a.addon_id] ?? a.price) });
      toast.success(`${a.name} updated`);
      onSaved();
    } catch {
      toast.error("Update failed");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/addons", { ...form, price: Number(form.price) });
      toast.success("Add-on created");
      setForm({ name: "", price: "", unit: "stay", description: "" });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create add-on");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "rounded-xl border border-ink/15 bg-cream px-3 py-2.5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none";

  return (
    <div className="mt-6 grid gap-5 lg:grid-cols-2" data-testid="admin-addons-list">
      {addons.map((a) => (
        <div key={a.addon_id} data-testid={`admin-addon-${a.slug}`} className="rounded-3xl border border-ink/8 bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-display text-lg font-medium text-ink">{a.name}</p>
              <p className="text-xs text-slate-500">{a.description}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">per {a.unit}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">₹</span>
              <input
                type="number"
                min="0"
                data-testid={`admin-addon-price-${a.slug}`}
                value={drafts[a.addon_id] ?? a.price}
                onChange={(e) => setDrafts((d) => ({ ...d, [a.addon_id]: e.target.value }))}
                className="w-24 rounded-xl border border-ink/15 bg-cream px-3 py-2 text-sm text-ink focus:border-bark focus:outline-none"
              />
              <button
                type="button"
                data-testid={`admin-addon-save-${a.slug}`}
                onClick={() => savePrice(a)}
                className="rounded-full bg-navy px-4 py-2 text-xs font-semibold text-cream transition-colors duration-200 hover:bg-ink"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ))}
      <form onSubmit={create} data-testid="admin-addon-create" className="rounded-3xl border border-dashed border-ink/15 bg-white/60 p-6">
        <p className="font-display text-lg font-medium text-ink">New add-on</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input required placeholder="Name" data-testid="admin-addon-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input required type="number" min="0" placeholder="Price ₹" data-testid="admin-addon-price-new" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
          <select value={form.unit} data-testid="admin-addon-unit" onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls}>
            <option value="stay">Per stay</option>
            <option value="day">Per day</option>
            <option value="night">Per night</option>
            <option value="session">Per session</option>
          </select>
          <input placeholder="Description" data-testid="admin-addon-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        </div>
        <button
          type="submit"
          data-testid="admin-addon-create-btn"
          disabled={busy}
          className="mt-4 rounded-full bg-bark px-6 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
        >
          {busy ? "Adding…" : "Add add-on"}
        </button>
      </form>
    </div>
  );
};

const RequestCard = ({ booking, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const b = booking;
  const cr = b.change_request;

  const resolveRequest = async (action) => {
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${b.booking_id}/change-request`, { action });
      toast.success(action === "approve" ? "Request approved" : "Request rejected");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resolve request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`admin-request-${b.booking_id}`} className="rounded-3xl border border-amberwarm/40 bg-amberwarm/5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-amberwarm/20 px-3 py-1 text-xs font-semibold text-amberwarm">
              {cr.type === "cancel" ? "Cancellation" : "Change"} request
            </span>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
              {STATUS_LABELS[b.status]}
            </span>
            <span className="text-xs text-slate-400">#{b.booking_id}</span>
          </div>
          <p className="mt-3 text-sm font-semibold text-ink">
            {b.customer?.name || "—"} · {b.pups.map((p) => p.name).join(", ")}
          </p>
          <p className="text-xs text-slate-500">
            {b.customer?.email}{b.customer?.phone ? ` · ${b.customer.phone}` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {b.items.map((i) => `${i.service_name} (${fmtDate(i.start_date)}${i.end_date !== i.start_date ? ` → ${fmtDate(i.end_date)}` : ""})`).join(" · ")}
          </p>
        </div>
        <p className="text-sm font-semibold text-ink">
          {fmtINR(b.amount_paid)} <span className="font-normal text-slate-400">/ {fmtINR(b.total)}</span>
        </p>
      </div>

      {cr.type === "cancel" && (
        <p className="mt-4 rounded-2xl bg-white p-4 text-sm text-slate-600">
          <span className="font-semibold text-ink">Refund estimate: {fmtINR(cr.refund_estimate)}</span>{" "}
          ({cr.refund_policy === "full" ? "100% — more than 24h before check-in" : "75% — within 24h of check-in"})
        </p>
      )}
      {cr.message && (
        <p className="mt-3 text-sm text-slate-600">Parent note: “{cr.message}”</p>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          data-testid={`admin-request-approve-${b.booking_id}`}
          onClick={() => resolveRequest("approve")}
          disabled={saving}
          className="rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-700 disabled:opacity-60"
        >
          Approve
        </button>
        <button
          type="button"
          data-testid={`admin-request-reject-${b.booking_id}`}
          onClick={() => resolveRequest("reject")}
          disabled={saving}
          className="rounded-full border border-ink/15 px-5 py-2.5 text-xs font-semibold text-navy transition-colors duration-200 hover:border-red-300 hover:text-red-500 disabled:opacity-60"
        >
          Reject
        </button>
      </div>
    </div>
  );
};

const Admin = () => {
  const [tab, setTab] = useState("Overview");
  const [overview, setOverview] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const pendingRequests = bookings.filter((b) => b.change_request?.status === "pending");

  const load = async () => {
    try {
      const [o, b, c, s, a] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/bookings"),
        api.get("/admin/customers"),
        api.get("/admin/services"),
        api.get("/addons"),
      ]);
      setOverview(o.data);
      setBookings(b.data);
      setCustomers(c.data);
      setServices(s.data);
      setAddons(a.data);
    } catch {
      toast.error("Could not load admin data");
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section data-testid="admin-panel" className="pt-32 pb-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
            <span className="h-px w-10 bg-bark" aria-hidden="true" />
            Account admin
          </p>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            The <em className="font-editorial italic text-bark">control room</em>
          </h1>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`admin-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                tab === t ? "bg-navy text-cream" : "border border-ink/10 text-navy/70 hover:text-navy"
              }`}
            >
              {t}
              {t === "Requests" && pendingRequests.length > 0 && (
                <span
                  data-testid="admin-requests-count"
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                    tab === t ? "bg-amberwarm text-navy" : "bg-amberwarm/20 text-amberwarm"
                  }`}
                >
                  {pendingRequests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "Overview" && overview && (
          <div className="mt-10">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Customers" value={overview.customers} />
              <Stat label="Pups" value={overview.pups} />
              <Stat label="Bookings" value={overview.bookings} />
              <Stat label="Collected" value={fmtINR(overview.collected)} />
            </div>
            <h2 className="mt-12 font-display text-2xl font-medium text-ink">Recent bookings</h2>
            <div className="mt-5 space-y-4">
              {overview.recent.map((b) => (
                <div key={b.booking_id} className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-ink/8 bg-white p-6">
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
                      {STATUS_LABELS[b.status]}
                    </span>
                    <p className="text-sm font-semibold text-ink">
                      {b.customer?.name} · {b.pups.map((p) => p.name).join(", ")}
                    </p>
                  </div>
                  <p className="text-sm text-slate-500">{b.items.map((i) => i.service_name).join(" · ")}</p>
                </div>
              ))}
              {overview.recent.length === 0 && <p className="text-sm text-slate-400">No bookings yet.</p>}
            </div>
          </div>
        )}

        {tab === "Bookings" && (
          <div className="mt-10 space-y-4" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <BookingRow key={b.booking_id} booking={b} onSaved={load} />
            ))}
            {bookings.length === 0 && <p className="text-sm text-slate-400">No bookings yet.</p>}
          </div>
        )}

        {tab === "Requests" && (
          <div className="mt-10 space-y-4" data-testid="admin-requests-list">
            {pendingRequests.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-ink/15 bg-white/60 py-16 text-center">
                <ShieldCheck className="h-9 w-9 text-slate-300" aria-hidden="true" />
                <p className="max-w-sm text-sm text-slate-500">
                  No pending cancellation or change requests. New requests from pet parents will show up here.
                </p>
              </div>
            ) : (
              pendingRequests.map((b) => (
                <RequestCard key={b.booking_id} booking={b} onSaved={load} />
              ))
            )}
          </div>
        )}

        {tab === "Customers" && (
          <div className="mt-10 space-y-4" data-testid="admin-customers-list">
            {customers.map((c) => (
              <CustomerCard key={c.user_id} c={c} onSaved={load} />
            ))}
            {customers.length === 0 && <p className="text-sm text-slate-400">No customers yet.</p>}
          </div>
        )}

        {tab === "Services" && (
          <>
            <div className="mt-10 grid gap-5 lg:grid-cols-2" data-testid="admin-services-list">
              {services.map((s) => (
                <ServiceEditor key={s.service_id} service={s} onSaved={load} />
              ))}
            </div>
            <h2 className="mt-14 font-display text-2xl font-medium text-ink">Add-ons</h2>
            <p className="mt-1 text-sm text-slate-500">
              Early drop, late pick-up, medications, caretaker room — offered during boarding and day
              care bookings.
            </p>
            <AddonManager addons={addons} onSaved={load} />
          </>
        )}
      </div>
    </section>
  );
};

export default Admin;
